import { IDropdownModel, DropdownModel } from "../dropdown/dropdown.model";

// Auto cover specs recorded by a tech against the "Auto Cover" equipment tracking record.
//
// Fields and option lists come from the Auto Cover spec sheet and use the vocabulary a tech reads off
// the equipment - NOT the Latham order vocabulary. The two overlap but don't match, so
// AutoCoverToLathamOrderService translates value by value when building a replacement quote.
export interface IAutoCoverSpecModel {
	equipmentTrackingAutoCoverId: number;
	companyId: number;
	equipmentTrackingId: number;

	// Mechanism
	manufacturer: string;
	manufacturerOther: string;
	model: string;
	unitLocation: string;
	poolType: string;
	motorSide: string;
	motorType: string;
	switchType: string;
	installationDate?: Date;
	lastCoverReplacementDate?: Date;

	// Fabric
	widthFeet?: number;
	widthInches?: number;
	lengthFeet?: number;
	lengthInches?: number;
	fabricColor: string;
	webbingType: string;
	ropeAttachment: string;
	trackType: string;
	leAttachment: string;
	leSize: string;
	// Only set when the tech overrides it. Left empty, the sheet shows Latham's standard slack for the
	// width and track type, and the replacement quote calculates the same value itself.
	slack?: number;
	// How far the track sits off the pool on each side, in inches. The motor side is measured
	// separately because the mechanism pushes that side out further.
	motorSideOffset?: number;
	nonMotorSideOffset?: number;

	// Electronics & components
	sliders: string;
	guideFeedType: string;
	waterFeature?: boolean;

	// Box & lid. Sizes are stored in inches and entered as feet and inches.
	housingType: string;
	housingWidth?: number;
	housingLength?: number;
	housingDepth?: number;
	lidType: string;

	// Stamped once a replacement quote has been created from these specs
	lathamOrderId?: number;
}

export class AutoCoverSpecModel implements IAutoCoverSpecModel {
	equipmentTrackingAutoCoverId: number = 0;
	companyId: number;
	equipmentTrackingId: number = 0;

	manufacturer: string;
	manufacturerOther: string;
	model: string;
	unitLocation: string;
	poolType: string;
	motorSide: string;
	motorType: string;
	switchType: string;
	installationDate?: Date;
	lastCoverReplacementDate?: Date;

	widthFeet?: number;
	widthInches?: number;
	lengthFeet?: number;
	lengthInches?: number;
	fabricColor: string;
	webbingType: string;
	ropeAttachment: string;
	trackType: string;
	leAttachment: string;
	leSize: string;
	slack?: number;
	motorSideOffset?: number;
	nonMotorSideOffset?: number;

	sliders: string;
	guideFeedType: string;
	waterFeature?: boolean;

	housingType: string;
	housingWidth?: number;
	housingLength?: number;
	housingDepth?: number;
	lidType: string;

	lathamOrderId?: number;
}

// The equipment name that carries the spec sheet. Must stay in sync with
// EquipmentTrackingModel.AutoCoverEquipmentName on the server.
export const AUTO_COVER_EQUIPMENT_NAME = "Auto Cover";

// The six manufacturers on the spec sheet. "Other" opens a free-text brand box and a free-text model.
export const AUTO_COVER_MANUFACTURER_OTHER = "Other";

export interface IAutoCoverColorSwatch {
	color: string;
	hex: string;
}

// Fabric Color is spec'd as "Color Selector From Ordering", so this is the same palette the Latham
// order's fabric color picker offers.
export const AUTO_COVER_FABRIC_COLORS: IAutoCoverColorSwatch[] = [
	{ color: "Navy", hex: "#083c8f" },
	{ color: "Royal Blue", hex: "#1d71c7" },
	{ color: "Light Blue", hex: "#a0cdf6" },
	{ color: "Tan", hex: "#dfd2b2" },
	{ color: "Mocha", hex: "#a38a61" },
	{ color: "Gray", hex: "#c4c3bf" },
	{ color: "Charcoal", hex: "#81817f" },
	{ color: "Black", hex: "#020202" },
	{ color: "Forest Green", hex: "#235530" },
	{ color: "Aqua", hex: "#0299aa" }
];

// Sliders and Guide Feeds are picked from pictures rather than a dropdown, because a tech recognises
// the part on the track far more reliably than its catalog name.
export interface IAutoCoverPictureOption {
	id: string;
	text: string;
	imageUrl?: string;
}

// The photos ship with the apps (libraries/assets/auto-cover, copied to assets/auto-cover by both
// angular.json builds) and are named after the option, so adding one is just dropping in a file named
// the way pictureUrl spells it. Until a photo exists the tile shows an icon instead.
const AUTO_COVER_PICTURE_ROOT = "assets/auto-cover";

const slug = (value: string): string => (value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const pictureUrl = (folder: string, name: string): string => `${AUTO_COVER_PICTURE_ROOT}/${folder}/${slug(name)}.jpg`;

// "None" is a real answer (no sliders on the cover) and has nothing to photograph
const sliderOption = (name: string): IAutoCoverPictureOption =>
	({ id: name, text: name, imageUrl: name === "None" ? undefined : pictureUrl("sliders", name) });

export const AUTO_COVER_SLIDER_OPTIONS: IAutoCoverPictureOption[] = [
	"None",
	"UG Detachable Rope",
	"Pre-403 UG",
	"Pre-403 UG Detachable",
	"801/403 Wire-on",
	"Residential UG",
	"Pre-801 Wire-on w/ Slider Plate"
].map(sliderOption);

export const AUTO_COVER_GUIDE_FEED_OTHER = "Other";

// Guide feeds differ by system and by whether it's an under track or a top track, so the picker only
// offers the few that fit. Built from the guide feed parts in the Latham parts catalog (a flush track
// counts as a top track, the way the Latham order classes it). Anything older or unusual is "Other";
// either way the value goes to the replacement quote's office notes, not onto the order.
const GUIDE_FEEDS: Record<string, { undertrack: string[], topTrack: string[] }> = {
	"Coverstar": {
		undertrack: ["403/801 Stainless", "418 Undertrack", "Residential UG", "Vanishing Edge"],
		topTrack: ["403 Top Track", "Pre-403 Top Track"]
	},
	"Pool Cover Specialist": {
		undertrack: ["PCS Underguide"],
		topTrack: ["PCS Topguide"]
	},
	"Coverpools": {
		undertrack: ["CP Underguide", "CP Underguide w/ Limit Hole"],
		topTrack: ["CP Topguide", "CP Topguide w/ Limit Hole"]
	},
	"APC": {
		undertrack: ["APC Small Channel", "APC Large Channel"],
		topTrack: ["APC Flush Track"]
	},
	"Aquamatic": {
		undertrack: ["Aquamatic Underguide"],
		topTrack: ["Aquamatic Topguide"]
	}
};

// ---- measurements ----

// A cover this company ordered for the job's home owner (see GetPreviousCovers)
export interface IAutoCoverPreviousCoverModel {
	lathamOrderId: number;
	orderId: string;
	submittedDate?: Date | string;
	trackSpaceFeet?: number;
	trackSpaceInches?: number;
	trackLengthFeet?: number;
	trackLengthInches?: number;
}

// How far a new width or length may be from a cover already ordered for the same home owner before the
// tech is asked to double check it
export const AUTO_COVER_MEASUREMENT_TOLERANCE_INCHES = 1;

// Feet and inches as one number of inches, or null when neither was recorded
export function autoCoverTotalInches(feet: number, inches: number): number {
	if ((feet === null || feet === undefined) && (inches === null || inches === undefined))
		return null;

	return (Number(feet) || 0) * 12 + (Number(inches) || 0);
}

// The reverse, for showing a stored number of inches in separate feet and inches boxes
export function splitAutoCoverInches(totalInches: number): { feet: number, inches: number } {
	if (totalInches === null || totalInches === undefined || isNaN(Number(totalInches)))
		return { feet: null, inches: null };

	const total = Number(totalInches);
	const feet = Math.floor(total / 12);

	return { feet, inches: Math.round((total - feet * 12) * 100) / 100 };
}

export function formatAutoCoverInches(totalInches: number): string {
	if (totalInches === null || totalInches === undefined)
		return "";

	const parts = splitAutoCoverInches(totalInches);
	return `${parts.feet}' ${parts.inches}"`;
}

// The question to ask before saving measurements that match none of the covers already ordered for this
// customer (newest first), or null when one of them matches within the tolerance or there's nothing to
// compare. Any match is enough, because an order doesn't say which pool or spa it was for. Only the
// dimensions recorded on both sides are compared, and the warning quotes the newest cover.
export function autoCoverMeasurementWarning(spec: IAutoCoverSpecModel, previousCovers: IAutoCoverPreviousCoverModel[]): string {
	if (!spec || !previousCovers?.length)
		return null;

	const width = autoCoverTotalInches(spec.widthFeet, spec.widthInches);
	const length = autoCoverTotalInches(spec.lengthFeet, spec.lengthInches);

	const close = (value: number, previous: number) => Math.abs(value - previous) <= AUTO_COVER_MEASUREMENT_TOLERANCE_INCHES;

	const comparable = previousCovers
		.map(cover => ({
			cover,
			width: autoCoverTotalInches(cover.trackSpaceFeet, cover.trackSpaceInches),
			length: autoCoverTotalInches(cover.trackLengthFeet, cover.trackLengthInches)
		}))
		.filter(x => (width !== null && x.width !== null) || (length !== null && x.length !== null));

	if (comparable.length === 0)
		return null;

	const matches = comparable.some(x =>
		(width === null || x.width === null || close(width, x.width)) &&
		(length === null || x.length === null || close(length, x.length)));

	if (matches)
		return null;

	const lastCover = comparable[0].cover;
	const lastWidth = comparable[0].width;
	const lastLength = comparable[0].length;

	const describe = (w: number, l: number) =>
		[w !== null ? `${formatAutoCoverInches(w)} W` : null, l !== null ? `${formatAutoCoverInches(l)} L` : null]
			.filter(x => !!x)
			.join(" x ");

	const submitted = lastCover.submittedDate ? new Date(lastCover.submittedDate) : null;
	const when = (submitted && !isNaN(submitted.getTime()))
		? `, ${(submitted.getMonth() + 1)}/${submitted.getDate()}/${submitted.getFullYear()}`
		: "";

	return `The last cover ordered for this customer (order ${lastCover.orderId}${when}) was ${describe(lastWidth, lastLength)}. ` +
		`You entered ${describe(width, length)}. Do you want to save it anyway?`;
}

const dd = (values: string[]): IDropdownModel[] => values.map(x => new DropdownModel(x, x));

// Option lists, straight from the Auto Cover spec sheet.
export class AutoCoverOptions {
	static readonly manufacturers: IDropdownModel[] = dd([
		"Coverstar", "Pool Cover Specialist", "Coverpools", "APC", "Aquamatic", AUTO_COVER_MANUFACTURER_OTHER
	]);

	static readonly unitLocations: IDropdownModel[] = dd(["Recessed", "Deck Mount"]);

	static readonly poolTypes: IDropdownModel[] = dd(["Gunite", "Vinyl Liner", "Fiberglass"]);

	static readonly motorSides: IDropdownModel[] = dd(["Left", "Right"]);

	static readonly motorTypes: IDropdownModel[] = dd(["Electric", "Hydraulic", "Electric HD"]);

	static readonly switchTypes: IDropdownModel[] = dd(["Toggle", "Key", "Touchpad"]);

	static readonly webbingTypes: IDropdownModel[] = dd(["Heat Sealed", "Sewn"]);

	static readonly trackTypes: IDropdownModel[] = dd(["Undertrack", "Top Track"]);

	static readonly lidTypes: IDropdownModel[] = dd([
		"Aluminum STD Lid", "Flat Lid", "Flush Lid", "Stone Lid W/ Brackets"
	]);

	static readonly housingTypes: IDropdownModel[] = dd([
		"Polymer/Molded Housing", "Concrete", "Wood"
	]);

	// How the fabric fastens to the leading edge bar
	static readonly leAttachments: IDropdownModel[] = dd(["Loop", "Rope"]);

	// The leading edge bar profile itself. Same physical descriptions the Latham order's Tubes >
	// Leading Edge dropdown uses, minus the model-specific naming - see AutoCoverToLathamOrderService.
	static readonly leSizes: IDropdownModel[] = dd([
		"1 Inch Flat", "1 3/4 Inch", "2 Inch", "2 1/2 Inch", "2 1/2 Inch Round",
		"3 Inch", "4 Inch", "5 Inch", "6 Inch", "Hidden LE"
	]);

	// Not on the spec sheet, kept at the customer's request. Narrowed by system and track type, see
	// GUIDE_FEEDS. Until the track type is known both lists are offered, and a system with no list
	// ("Other") gets a generic choice per track type.
	static guideFeedsFor(manufacturer: string, trackType: string): IAutoCoverPictureOption[] {
		const lists = GUIDE_FEEDS[manufacturer];
		const undertrack = trackType !== "Top Track";
		const topTrack = trackType !== "Undertrack";

		let names: string[];
		if (lists)
			names = [...(undertrack ? lists.undertrack : []), ...(topTrack ? lists.topTrack : [])];
		else
			names = [...(undertrack ? ["Undertrack Guide Feed"] : []), ...(topTrack ? ["Top Track Guide Feed"] : [])];

		const options: IAutoCoverPictureOption[] = names.map(name => ({
			id: name,
			text: name,
			// Only the catalog parts have photos; the generic choices have nothing specific to show. The
			// names are unique across systems, so the photo is named after the guide feed alone.
			imageUrl: lists ? pictureUrl("guide-feeds", name) : undefined
		}));

		options.push({ id: AUTO_COVER_GUIDE_FEED_OTHER, text: AUTO_COVER_GUIDE_FEED_OTHER });

		return options;
	}

	// Models are manufacturer specific. "Other" has no list: the model becomes a free-text box.
	static modelsForManufacturer(manufacturer: string): IDropdownModel[] {
		switch (manufacturer) {
			case "Coverstar":
				return dd(["Eclipse (Stainless)", "CS3000 (Aluminum)", "CS300HD Spa", "Atom"]);
			case "Pool Cover Specialist":
				return dd(["Infinity 4000"]);
			case "Coverpools":
				return dd(["T4", "T3 Evo", "Save-T 3", "CP2"]);
			case "APC":
				return dd(["APC365"]);
			case "Aquamatic":
				return dd(["Hydramatic"]);
			default:
				return [];
		}
	}

	// Rope Attachment is the one option list that genuinely differs per manufacturer on the sheet.
	static ropeAttachmentsForManufacturer(manufacturer: string): IDropdownModel[] {
		switch (manufacturer) {
			case "Coverstar":
			case "Pool Cover Specialist":
				return dd(["Continuous", "Continuous w/ Tab", "Detachable"]);
			case "Coverpools":
				return dd(["Continuous", "Steel cable"]);
			case "APC":
			case "Aquamatic":
				return dd(["Continuous"]);
			default:
				return dd(["Continuous", "Detachable"]);
		}
	}
}
